/**
 * moment → dayjs 兼容 shim
 *
 * 通过 webpack alias 将 `import moment from 'moment'` 重定向到这里，
 * 使业务代码零改动，同时把 ~72KB 的 moment 从 bundle 中彻底移除。
 *
 * 设计原则：
 *   dayjs 设计之初即对齐 moment 的实例方法 API，所以不需要手动包装每个方法。
 *   只需要：
 *     1. 预加载所有 dayjs 官方 plugin（覆盖需要插件才支持的 API）
 *     2. 在 dayjs 函数上补充 moment 特有的静态方法（duration / isMoment / utc 等）
 *     3. 直接导出 dayjs —— 它就是 moment
 *
 *   好处：后续业务新用到任何 dayjs 已支持的 moment API，无需修改此文件。
 */

import dayjs from 'dayjs';

// ---- 预加载所有常用 dayjs 插件 ----
import customParseFormat from 'dayjs/plugin/customParseFormat'; // moment(val, fmt) 带格式解析
import duration from 'dayjs/plugin/duration'; // moment.duration()
import relativeTime from 'dayjs/plugin/relativeTime'; // .fromNow() / .to() / .from()
import isBetween from 'dayjs/plugin/isBetween'; // .isBetween()
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'; // .isSameOrBefore()
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'; // .isSameOrAfter()
import weekOfYear from 'dayjs/plugin/weekOfYear'; // .week()
import weekYear from 'dayjs/plugin/weekYear'; // .weekYear()
import weekday from 'dayjs/plugin/weekday'; // .weekday()
import localeData from 'dayjs/plugin/localeData'; // .localeData() / .months() 等
import advancedFormat from 'dayjs/plugin/advancedFormat'; // 扩展 format token（Q/x/X/k 等）
import localizedFormat from 'dayjs/plugin/localizedFormat'; // locale 本地化 token（L/LL/LLL/LLLL/LT/LTS）
import quarterOfYear from 'dayjs/plugin/quarterOfYear'; // .quarter()
import minMax from 'dayjs/plugin/minMax'; // dayjs.max() / dayjs.min()
import toArray from 'dayjs/plugin/toArray'; // .toArray()
import toObject from 'dayjs/plugin/toObject'; // .toObject()
import utc from 'dayjs/plugin/utc'; // moment.utc() / .utcOffset()

// 加载中文 locale 用于PunchClock 周几显示
import 'dayjs/locale/zh-cn';

dayjs.extend(customParseFormat);
dayjs.extend(duration);
dayjs.extend(relativeTime);
dayjs.extend(isBetween);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(weekOfYear);
dayjs.extend(weekYear);
dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.extend(advancedFormat);
dayjs.extend(localizedFormat);
dayjs.extend(quarterOfYear);
dayjs.extend(minMax);
dayjs.extend(toArray);
dayjs.extend(toObject);
dayjs.extend(utc);

// ---- moment.ISO_8601 常量标记 ----
// 用于在格式数组中识别 ISO 8601 解析请求
const ISO_8601 = 'ISO_8601';

// ---- 兼容包装函数 ----
// dayjs 实例方法与 moment 完全对齐，不需要包装。
// 但调用签名有两个 moment 特有的边界需要处理：
//
//   1. 格式数组：moment(val, ['fmt1', 'fmt2', moment.ISO_8601], strict)
//      customParseFormat 只支持单个格式，需要逐一尝试。
//
//   2. moment.ISO_8601 常量：作为数组元素时表示"回退到原生 ISO 解析"。
//
// 其余情况（无格式、单格式、strict 模式）直接透传给 dayjs，行为一致。
function moment(date, format, strict) {
  if (Array.isArray(format)) {
    for (const fmt of format) {
      let d;
      if (fmt === ISO_8601) {
        // ISO_8601 标记：用 dayjs 原生解析（天然支持 ISO 8601）
        d = dayjs(date);
      } else {
        d = strict ? dayjs(date, fmt, true) : dayjs(date, fmt);
      }
      if (d.isValid()) return d;
    }
    // 所有格式都无法解析，返回一个 invalid dayjs 对象（与 moment 行为一致）
    return dayjs(null);
  }

  if (format !== undefined) {
    // 标量 ISO_8601：与数组分支保持一致，回退到 dayjs 原生 ISO 解析，
    // 否则会把 'ISO_8601' 当字面格式传给 customParseFormat 得到 Invalid
    if (format === ISO_8601) {
      return dayjs(date);
    }
    return strict ? dayjs(date, format, true) : dayjs(date, format);
  }

  return dayjs(date);
}

// ---- 把 dayjs 的静态方法挂到包装函数上 ----
// Object.assign 拷贝 dayjs 的「可枚举自有属性」，dayjs 核心与各 plugin 注入的静态方法
// （unix / utc / duration / isDayjs / max / min / locale / extend 等）均为可枚举自有属性，
// 因此能被一并拷贝到包装函数上，使 moment.xxx 与 dayjs.xxx 等价。
Object.assign(moment, dayjs);

// moment.fn 是 moment 插件系统的原型扩展入口
// 部分第三方库会通过 moment.fn.xxx = ... 注入实例方法
moment.fn = dayjs.prototype;

// moment 特有的静态常量 / 方法补充
moment.ISO_8601 = ISO_8601;
moment.isMoment = dayjs.isDayjs;

export default moment;



// WEBPACK FOOTER //
// ./src/utils/dateTime/momentShim.js